import { Dispatch, PureComponent, ReactNode, SetStateAction, useEffect } from 'react';
import { connect } from 'react-redux';
import { DatePicker, Input } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import moment, { Moment } from 'moment';
import { ColumnProps } from 'antd/lib/table';
import TableWithDragableRows, {
    DragHandle,
} from '../../../..//prescriptionPad/rightPanel/workspace/padV2/TableWithDragableRows';
import {
    EditableCell,
    EditableCellV2,
} from '../../../../prescriptionPad/rightPanel/workspace/padV2/MedTableRenderer';
import { buildOptions } from '../../../../prescriptionPad/rightPanel/workspace/padV2/SymDiaTableV2';
import { track } from '../../../../../tracking';
import AntSelect from '../../../../prescriptionPad/rightPanel/workspace/padV2/AntSelect';
import { FAMILY_MEMBERS_NAME, LOCAL_ID_PREFIX, STATUS_VALUES } from '../../../../../constants';
import { headerStyle } from '../../../../common/TableUtils';
import {
    FamilyHistory,
    MEDICAL_HISTORY_CATEGORY,
    MHC,
    MHFrequency,
    MHItem2,
    MHSince,
    PastProcedures,
} from '../../../../../store/types/medicalHistory';
import {
    generateFrequencyOptions,
    generateSinceOptions,
    generateStatusOptions,
} from '../../../../common/optionGeneratorUtils';
import { medicalHistoryOptions } from '../../../../medicalHistoryConfigPanel/CustomiseSections';
import { randomString } from '../../../../../store/utils';
import { EkaFocosableElement } from '../../../../common/types/EkaFocusable';
import { MEDICAL_HISTORY_CONTAINER } from '../MedicalHistoryContainer';

type Props = {
    elements: MHItem2[];
    containerId: string;
    onElementSelect: (index: number, item: MHItem2) => void;
    onElementUpdate: (item: MHItem2) => void;
    onElementDelete: (itemId: string) => void;
    onDrag: ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => void;
    catId: MEDICAL_HISTORY_CATEGORY;
};

type State = {
    tableColumns: ColumnProps<MHItem2>[];
    tableColumnRefArray: { [key: number]: number };
};

class MedicalHistoryTable extends PureComponent<Props, State> {
    static REF_MAP: EkaFocosableElement[][] = [];
    TABLE_ID = 'medical-history-table';
    state: State = {
        tableColumns: [],
        tableColumnRefArray: {},
    };

    OptionIcon = (<div>{medicalHistoryOptions[this.props.catId].searchIcon()}</div>);

    getStrValueAtIndex = (element: MHItem2, index: number): string | undefined => {
        switch (index) {
            case 0:
                return element.name;
            case 1:
                return (element as MHSince)?.since?.custom;
            case 2:
                return (element as MHFrequency)?.frequency?.custom;
            case 3:
                return element.notes;
            case 4:
                return (element as FamilyHistory).who;
            case 5:
                return element.status;
            case 6:
                return (element as PastProcedures)?.on?.yyyy;
        }

        return '';
    };

    handleNextFocus = (rowIndex: number, columnIndex: number, isFieldEmpty?: boolean): void => {
        const { elements } = this.props;

        const element = elements[rowIndex];

        if (!element) {
            return;
        }

        if (columnIndex < 7) {
            if (isFieldEmpty) {
                return;
            }

            const nextIndex = this.state.tableColumnRefArray[columnIndex];
            if (this.getStrValueAtIndex(element, nextIndex)) {
                return;
            }

            if (nextIndex && MedicalHistoryTableComponent.REF_MAP[rowIndex][nextIndex]) {
                MedicalHistoryTableComponent.REF_MAP[rowIndex][nextIndex]?.focus();
            }
        }
    };

    findIndexForRecordKey = (key: string): number => {
        return this.props.elements.findIndex((e, index) => this.rowKeyFromId(e.id, index) === key);
    };

    elementSuggestApi = async (text: string): Promise<MHItem2[]> => {
        try {
            const res = await medicalHistoryOptions[this.props.catId]?.func?.(text);
            return res || [];
        } catch (e) {
            track('ERROR', {
                on: 'MedicalHistoryTable#elementSuggestApi',
                error: (e as Error).message,
            });
            return [];
        }
    };

    createOption = (value: string) => ({
        id: `${LOCAL_ID_PREFIX}${new Date().getTime()}-${randomString(4)}`,
        name: value.trim(),
    });

    handleSearch = async (value: string): Promise<MHItem2[]> => {
        const { elements } = this.props;

        const all = await buildOptions<MHItem2>(
            value,
            elements,
            [],
            this.elementSuggestApi,
            undefined,
            this.createOption,
            false,
        );
        return all;
    };

    onSearch = async (text: string): Promise<{ id: string; label: string }[]> => {
        return new Promise((resolve) => {
            resolve(generateSinceOptions(text));
        });
    };

    onFrequencySearch = async (text: string): Promise<{ id: string; label: string }[]> => {
        return new Promise((resolve) => {
            resolve(generateFrequencyOptions(text));
        });
    };

    renderCellAutosuggestItem = (option: MHItem2) => {
        return (
            <RenderCellAutosuggestItemJsx
                option={option}
                wrapperClassName={'sy-dropdown-item'}
                icon={this.OptionIcon}
                iconWrapperClassName={'circle-sy'}
            />
        );
    };

    setPropertyInputRef = (ref: EkaFocosableElement, elIndex: number, propertyIndex: number) => {
        if (!MedicalHistoryTableComponent.REF_MAP[elIndex]) {
            MedicalHistoryTableComponent.REF_MAP[elIndex] = [];
        }

        MedicalHistoryTableComponent.REF_MAP[elIndex][propertyIndex] = ref;
        return;
    };

    onStatus = () => {
        return generateStatusOptions;
    };

    disabledFutureDate = (current: Moment): boolean => {
        const customDate = moment().add(1, 'day').format('YYYY-MM-DD');
        return current && current >= moment(customDate, 'YYYY-MM-DD');
    };

    Name = {
        onHeaderCell: headerStyle,
        key: 'medical-history-row-name',
        className: 'medical-history-row-name',
        render: (_: any, __: MHItem2, index: number) => {
            const { elements, catId } = this.props;

            return (
                <div className="flex space-x-16 items-center">
                    <DragHandle />
                    <div
                        className={`font-400 w-full text-neutral-black text-14 placeholder-neutral-6`}
                    >
                        <EditableCell
                            fieldName={`mhc-${index}`}
                            label={medicalHistoryOptions[catId].name}
                            setRef={(ref) => {
                                this.setPropertyInputRef(ref, index, 0);
                            }}
                            debounceDelay={200}
                            customDropdownClass={`py-8 ${'lx-reading-dropdown'} rounded-8`}
                            customDropdownWidth={350}
                            optionsRenderer={this.renderCellAutosuggestItem}
                            onSearch={this.handleSearch}
                            getLabelToCompare={(o) => o.name}
                            selectedValue={
                                elements[index] && elements[index].id ? elements[index] : undefined
                            }
                            onSelect={(option) => {
                                option && this.props.onElementSelect(index, option);
                                this.handleNextFocus(index, 0, option ? false : true);
                            }}
                            containerId={this.props.containerId}
                            placeholder={`Type property`}
                        />
                    </div>
                </div>
            );
        },
    };
    Notes = {
        onHeaderCell: headerStyle,
        key: 'medical-history-row-notes',
        className: 'medical-history-row-notes',
        render: (_: any, el: MHItem2, index: number) => {
            const value = el.notes || '';
            return (
                <div className="flex space-x-8 items-center">
                    {this.props.catId === 'recentTravelHistory' && <DragHandle />}
                    <EditableCellV2
                        defaultValue={value}
                        onBlur={(e) => {
                            this.props.onElementUpdate({
                                ...el,
                                notes: e.target.value,
                            });
                            this.handleNextFocus(index, 3, e.target.value ? false : true);
                        }}
                        setRef={(ref) => {
                            this.setPropertyInputRef(ref, index, 3);
                            return;
                        }}
                        label="Add notes here"
                    >
                        <Input.TextArea
                            autoFocus
                            defaultValue={value}
                            rows={2}
                            placeholder="Add notes here"
                        />
                    </EditableCellV2>
                    <DeleteOutlined
                        style={{ color: '#616A8F' }}
                        size={24}
                        onClick={() => this.props.onElementDelete(el.id)}
                        onPointerLeaveCapture={undefined}
                    />
                </div>
            );
        },
    };
    Since = {
        onHeaderCell: headerStyle,
        key: 'medical-history-row-since',
        className: 'medical-history-row-since',
        render: (_: any, el: MHSince, index: number) => {
            return (
                <EditableCell
                    containerId={this.props.containerId}
                    fieldName={`mhc-${index}`}
                    label="Since"
                    setRef={(ref) => {
                        this.setPropertyInputRef(ref, index, 1);
                    }}
                    withoutError
                    customDropdownClass="font-lato"
                    placeholder="Since"
                    debounceDelay={0}
                    onSearch={this.onSearch}
                    selectedValue={{
                        id: el?.since?.custom || '',
                        label: el?.since?.custom || '',
                    }}
                    getLabelToCompare={(o) => o.label}
                    onSelect={(option) => {
                        this.props.onElementUpdate({
                            ...el,
                            since: { custom: option?.label || '' },
                        });
                        this.handleNextFocus(index, 1, option ? false : true);
                    }}
                />
            );
        },
    };
    Frequency = {
        onHeaderCell: headerStyle,
        key: 'medical-history-row-frequency',
        className: 'medical-history-row-frequency',
        render: (_: any, el: MHFrequency, index: number) => {
            return (
                <EditableCell
                    containerId={this.props.containerId}
                    fieldName={`mhc-${index}`}
                    label="Frequency"
                    setRef={(ref) => {
                        this.setPropertyInputRef(ref, index, 2);
                    }}
                    withoutError
                    customDropdownClass="font-lato"
                    placeholder="times a day"
                    debounceDelay={0}
                    onSearch={this.onFrequencySearch}
                    selectedValue={{
                        id: el?.frequency?.custom || '',
                        label: el?.frequency?.custom || '',
                    }}
                    getLabelToCompare={(o) => o.label}
                    onSelect={(option) => {
                        this.props.onElementUpdate({
                            ...el,
                            frequency: { custom: option?.label || '' },
                        });
                        this.handleNextFocus(index, 2, option?.label ? false : true);
                    }}
                />
            );
        },
    };
    ParentConditions = {
        onHeaderCell: headerStyle,
        key: 'medical-history-row-parentConditions',
        className: 'medical-history-row-parentConditions',
        render: (_: any, el: FamilyHistory, index: number) => {
            return (
                <div className="flex space-x-8 items-center">
                    <EditableCellV2
                        defaultValue={el.who || ''}
                        label={'Family Member'}
                        setRef={(ref) => this.setPropertyInputRef(ref, index, 4)}
                    >
                        <AntSelect
                            containerIdToAttachDropdown={this.props.containerId}
                            className="bg-transparent"
                            showArrow={false}
                            defaultOpen
                            style={{ width: '100%' }}
                            autoFocus
                            allowClear
                            placeholder={'Family Member'}
                            optionFilterProp="children"
                            options={FAMILY_MEMBERS_NAME}
                            setValue={(option) => {
                                option &&
                                    this.props.onElementUpdate({
                                        ...el,
                                        who: option.value ? option.value : undefined,
                                    });
                                this.handleNextFocus(index, 4, option?.value ? false : true);
                            }}
                            filterOption={true}
                            getValue={(o) => o?.id}
                            getLabelToCompare={(o) => o.value}
                        />
                    </EditableCellV2>
                </div>
            );
        },
    };
    Status = {
        onHeaderCell: headerStyle,
        key: 'medical-history-row-status',
        className: 'medical-history-row-status',
        render: (_: any, el: MHItem2, index: number) => {
            const selectedStatus = STATUS_VALUES.find((val) => val.value === el.status);
            return (
                <div className="flex space-x-8 items-center">
                    <EditableCellV2
                        defaultValue={selectedStatus?.label || ''}
                        label={'active/inactive'}
                        setRef={(ref) => this.setPropertyInputRef(ref, index, 5)}
                    >
                        <AntSelect
                            containerIdToAttachDropdown={this.props.containerId}
                            className="bg-transparent"
                            showArrow={false}
                            value={STATUS_VALUES.find((val) => val.value === el.status)}
                            defaultOpen
                            autoFocus
                            allowClear={true}
                            style={{ width: '100%' }}
                            placeholder={'active/inactive'}
                            optionFilterProp="children"
                            options={STATUS_VALUES}
                            setValue={(option) => {
                                this.props.onElementUpdate({
                                    ...el,
                                    status: option?.value ? option?.value : undefined,
                                });
                                this.handleNextFocus(index, 5, option?.value ? false : true);
                            }}
                            filterOption={true}
                            getValue={(o) => o?.id}
                            getLabelToCompare={(o) => o.label}
                        />
                    </EditableCellV2>
                </div>
            );
        },
    };
    Date = {
        onHeaderCell: headerStyle,
        key: 'medical-history-row-date',
        className: 'medical-history-row-date',
        render: (_: any, el: PastProcedures, index: number) => {
            return (
                <div>
                    <EditableCellV2
                        defaultValue={el.on ? `${el.on.yyyy}/${el.on.mm}` : ''}
                        label={'Date'}
                        setRef={(ref) => this.setPropertyInputRef(ref, index, 6)}
                    >
                        <DatePicker
                            id={this.props.containerId}
                            autoFocus
                            picker={'month'}
                            onChange={(t) => {
                                this.props.onElementUpdate({
                                    ...el,
                                    on: {
                                        yyyy: moment(t).format('YYYY'),
                                        mm: moment(t).format('MM'),
                                    },
                                });
                                this.handleNextFocus(index, 6, t ? false : true);
                            }}
                            style={{ width: '100%' }}
                            allowClear={false}
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            value={el.on ? moment(`${el.on.yyyy}/${el.on.mm}`, 'YYYY/MM') : null}
                            disabledDate={this.disabledFutureDate}
                            format="YYYY/MM"
                            getPopupContainer={() =>
                                this.props.containerId
                                    ? (document.getElementById(
                                          this.props.containerId,
                                      ) as HTMLElement)
                                    : document.body
                            }
                        />
                    </EditableCellV2>
                </div>
            );
        },
    };

    setupColumns = () => {
        const { catId } = this.props;
        switch (catId) {
            case MEDICAL_HISTORY_CATEGORY.PATIENT_MEDICAL_CONDITIONS:
            case MEDICAL_HISTORY_CATEGORY.FOOD_OTHER_ALLERGY:
            case MEDICAL_HISTORY_CATEGORY.DRUG_ALLERGY:
                this.setState({
                    tableColumns: [this.Name, this.Since, this.Status, this.Notes],
                    tableColumnRefArray: { 0: 1, 1: 5, 5: 3 },
                });
                break;

            case MEDICAL_HISTORY_CATEGORY.LIFESTYLE_HABITS:
            case MEDICAL_HISTORY_CATEGORY.CURRENT_MEDICATIONS:
                this.setState({
                    tableColumns: [this.Name, this.Since, this.Frequency, this.Status, this.Notes],
                    tableColumnRefArray: { 0: 1, 1: 2, 2: 5, 5: 3 },
                });
                break;
            case MEDICAL_HISTORY_CATEGORY.FAMILY_HISTORY:
                this.setState({
                    tableColumns: [this.Name, this.ParentConditions, this.Status, this.Notes],
                    tableColumnRefArray: { 0: 4, 4: 5, 5: 3 },
                });
                break;
            case MEDICAL_HISTORY_CATEGORY.PAST_PROCEDURES:
                this.setState({
                    tableColumns: [this.Name, this.Date, this.Status, this.Notes],
                    tableColumnRefArray: { 0: 6, 6: 5, 5: 3 },
                });
                break;

            case MEDICAL_HISTORY_CATEGORY.OTHER_MEDICAL_HISTORY:
                this.setState({
                    tableColumns: [this.Name, this.Notes],
                    tableColumnRefArray: { 0: 3 },
                });
                break;
            case MEDICAL_HISTORY_CATEGORY.RECENT_TRAVEL_HISTORY:
                this.setState({ tableColumns: [this.Notes], tableColumnRefArray: {} });
                break;
        }
    };

    componentDidMount() {
        this.setupColumns();
    }

    rowKeyFromId = (id: string, index?: number): string =>
        `${'medical-history'}-${index}-${(id || []).length > 0}`;

    render(): ReactNode {
        const { elements, onDrag } = this.props;
        const { tableColumns } = this.state;

        if (elements.length === 0) {
            return null;
        }

        return (
            <TableWithDragableRows
                showHeader={false}
                id={this.TABLE_ID}
                tableLayout="fixed"
                className="mt-8 lx-reading-table"
                rowClassName={() => 'editable-row'}
                bordered
                dataSource={elements}
                columns={tableColumns}
                pagination={false}
                rowKey={(record, index) => this.rowKeyFromId(record.id, index)}
                findIndexForRecordKey={this.findIndexForRecordKey}
                onSortEnd={onDrag}
            />
        );
    }
}
export const MH_POP_UP = 'MH_POP_UP';

export const MedicalHistoryTableComponent = connect()(MedicalHistoryTable);

const PadV2MedicalHistoryCommonItemsTablePadV2: React.FC<{
    catId: MEDICAL_HISTORY_CATEGORY;
    isMedicalHistorySection?: boolean;
    patientHistoryData: MHC | undefined;
    tableRefActive: boolean;
    setTableRefActive: Dispatch<SetStateAction<boolean>>;
    setMedicalHistoryCommonItemsSectionData: (
        id: MEDICAL_HISTORY_CATEGORY,
        item: MHItem2,
        index?: number,
    ) => void;
    deleteMedicalHistoryCommonItemsSectionData: (
        catId: MEDICAL_HISTORY_CATEGORY,
        itemId: string,
    ) => void;
    swapMedicalHistoryCommonItemsSectionData: (
        catId: MEDICAL_HISTORY_CATEGORY,
        from: number,
        to: number,
    ) => void;
}> = ({
    catId,
    isMedicalHistorySection,
    patientHistoryData,
    tableRefActive,
    setTableRefActive,
    setMedicalHistoryCommonItemsSectionData,
    deleteMedicalHistoryCommonItemsSectionData,
    swapMedicalHistoryCommonItemsSectionData,
}) => {
    const TARGET_CONTAINER = isMedicalHistorySection ? MEDICAL_HISTORY_CONTAINER : MH_POP_UP;

    const elements = (patientHistoryData?.[catId] as MHItem2[]) || [];

    useEffect(() => {
        if (tableRefActive) {
            const rowRef = MedicalHistoryTableComponent.REF_MAP[elements.length - 1];
            if (rowRef && medicalHistoryOptions?.[catId].initialRef) {
                rowRef[medicalHistoryOptions[catId].initialRef as number]?.focus();
            }
            setTableRefActive(false);
        }
    }, [elements.length]);

    const onElementSelect = (index: number, item: MHItem2): void => {
        const data = {
            id: item.id,
            name: item.name,
            status: item.status,
        };

        catId && setMedicalHistoryCommonItemsSectionData(catId, data, index);
    };
    const onElementUpdate = (item: MHItem2): void => {
        catId && setMedicalHistoryCommonItemsSectionData(catId, item);
    };
    const onElementDelete = (itemId: string): void => {
        deleteMedicalHistoryCommonItemsSectionData(catId, itemId);
    };

    const onDrag = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }): void => {
        if (oldIndex !== newIndex) {
            swapMedicalHistoryCommonItemsSectionData(catId, oldIndex, newIndex);
        }
    };

    return (
        <MedicalHistoryTableComponent
            onElementUpdate={onElementUpdate}
            onElementDelete={onElementDelete}
            onElementSelect={onElementSelect}
            elements={elements}
            containerId={TARGET_CONTAINER}
            onDrag={onDrag}
            catId={catId}
        />
    );
};

export default PadV2MedicalHistoryCommonItemsTablePadV2;

export const RenderCellAutosuggestItemJsx: React.FC<{
    option: { name: string };
    wrapperClassName?: string;
    iconWrapperClassName?: string;
    icon: JSX.Element | string;
}> = ({ option, wrapperClassName, icon, iconWrapperClassName }) => {
    return (
        <div
            className={`flex items-center justify-between ${wrapperClassName} cursor-pointer font-lato w-full`}
        >
            <div className="flex items-center space-x-8">
                <div className={iconWrapperClassName}>
                    <p>{icon}</p>
                </div>
                <p className="text-neutral-1 text-14 w-full">{option.name}</p>
            </div>
        </div>
    );
};
